import MediaImage from "@/components/platform/MediaImage";
import { useRef, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Send, Trash2, Loader2, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";
import { useAuthors } from "@/hooks/platform/useAuthors";
import { usePostComments, useAddComment, useDeleteComment } from "@/hooks/platform/useComments";
import { useMediaUpload } from "@/hooks/platform/useMediaUpload";
import MentionTextarea, { MentionText, extractMentions } from "@/components/platform/MentionTextarea";
import AuthorChip from "@/components/platform/AuthorChip";
import MediaCarousel from "@/components/platform/MediaCarousel";
import { COMMENT_LIMIT, findProfanity } from "@/lib/postValidation";

/** Image attachments are stored on their own lines so text-only clients still read fine. */
const IMG_PREFIX = "[img]";

const splitComment = (body: string) => {
  const images: string[] = [];
  const text: string[] = [];
  body.split("\n").forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith(IMG_PREFIX)) images.push(trimmed.slice(IMG_PREFIX.length));
    else text.push(line);
  });
  return { text: text.join("\n").trim(), images };
};

/** Twitter-style comment thread for a feed post, with @mention and image replies. */
const PostComments = ({ postId }: { postId: string }) => {
  const { userId } = useCurrentUser();
  const { data: comments = [], isLoading } = usePostComments(postId);
  const addComment = useAddComment();
  const deleteComment = useDeleteComment();
  const { upload, uploading } = useMediaUpload("creator-media");
  const [draft, setDraft] = useState("");
  const [images, setImages] = useState<{ url: string; file: File }[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const { getAuthor } = useAuthors([...comments.map((c) => c.author_id), userId]);

  const pick = (files: FileList | null) => {
    if (!files) return;
    const picked = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .slice(0, 4 - images.length)
      .map((file) => ({ url: URL.createObjectURL(file), file }));
    setImages((prev) => [...prev, ...picked]);
  };

  const submit = async () => {
    const text = draft.trim();
    if ((!text && images.length === 0) || !userId) return;
    if (text.length > COMMENT_LIMIT) {
      toast.error(`Keep replies under ${COMMENT_LIMIT} characters.`);
      return;
    }
    const bad = findProfanity(text);
    if (bad.length) {
      toast.error(`Please remove offensive language: ${bad.join(", ")}.`);
      return;
    }

    const uploaded: string[] = [];
    for (const img of images) {
      const url = await upload(img.file);
      if (url) uploaded.push(url);
    }
    const body = [text, ...uploaded.map((u) => `${IMG_PREFIX}${u}`)].filter(Boolean).join("\n");

    addComment.mutate(
      { postId, authorId: userId, body, mentions: extractMentions(text) },
      {
        onSuccess: () => { setDraft(""); setImages([]); },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Comment failed"),
      },
    );
  };

  return (
    <div className="pt-3 mt-2 border-t border-border space-y-3">
      {isLoading ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" />Loading comments…</p>
      ) : comments.length === 0 ? (
        <p className="text-xs text-muted-foreground">No comments yet — be the first to reply.</p>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => {
            const { text, images: commentImages } = splitComment(c.body);
            return (
              <div key={c.id} className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <AuthorChip
                    author={{ ...getAuthor(c.author_id), meta: formatDistanceToNow(new Date(c.created_at), { addSuffix: true }) }}
                    size="sm"
                  />
                  {text && <MentionText text={text} className="block text-sm mt-1 whitespace-pre-wrap break-words" />}
                  {commentImages.length > 0 && (
                    <MediaCarousel media={commentImages} className="mt-2 max-w-xs" heightClass="max-h-56" />
                  )}
                </div>
                {userId === c.author_id && (
                  <button
                    aria-label="Delete comment"
                    className="text-muted-foreground hover:text-red-400 shrink-0"
                    onClick={() => deleteComment.mutate(c.id, { onSuccess: () => toast.success("Comment removed") })}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {userId ? (
        <div className="space-y-2">
          {images.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {images.map((img, i) => (
                <div key={img.url} className="relative">
                  <MediaImage src={img.url} alt={`Attachment ${i + 1}`} className="w-16 h-16 rounded-lg object-cover" />
                  <button
                    aria-label="Remove image"
                    onClick={() => setImages((prev) => prev.filter((p) => p.url !== img.url))}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-background border border-border flex items-center justify-center"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <MentionTextarea
                value={draft}
                onChange={setDraft}
                onSubmit={submit}
                placeholder="Reply… use @ to mention someone"
                rows={1}
                className="min-h-[42px] max-h-32 resize-none rounded-2xl border-border"
              />
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => pick(e.target.files)} />
            <Button
              size="icon"
              variant="ghost"
              aria-label="Add image to reply"
              onClick={() => fileRef.current?.click()}
              disabled={images.length >= 4}
            >
              <ImagePlus className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              onClick={submit}
              disabled={(!draft.trim() && images.length === 0) || addComment.isPending || uploading}
              aria-label="Send comment"
            >
              {addComment.isPending || uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Sign in to join the conversation.</p>
      )}
    </div>
  );
};

export default PostComments;
